/**
 * TalarMedical Authentication System - Login Component
 * 
 * Professional login interface with A2B Business Consulting branding
 * Features: JSON credential validation, loading states, error handling
 * 
 * Created for: TalarMedical-A2B-DataProject-MVP
 * Phase: 2 - Secure Authentication Implementation
 */

import React, { useState } from 'react';

interface LoginProps {
  onLogin: (isAuthenticated: boolean, userInfo?: any) => void;
}

/**
 * Login Component
 * 
 * Provides secure authentication interface with A2B branding
 * Validates credentials against JSON file and manages login state
 */
const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle login form submission
   * 
   * Validates credentials against JSON file and manages authentication flow
   * Includes loading states and error handling for user feedback
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Simulate network delay for professional loading experience (1.5 seconds)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Fetch user credentials from JSON file (frontend-only auth for Phase 2)
      const response = await fetch('/credentials.json');
      const data = await response.json();
      
      // Find matching user in credentials database
      const user = data.users.find(
        (u: any) => u.username === username && u.password === password
      );

      if (user) {
        // Successful authentication - pass user info to parent component
        onLogin(true, user);
      } else {
        // Invalid credentials - show error message
        setError('Invalid username or password');
        setIsLoading(false);
      }
    } catch (err) {
      // Network or parsing error - show generic error
      setError('Authentication failed. Please try again.');
      setIsLoading(false);
    }
  };

  // Loading State: Professional authentication screen with A2B branding
  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          {/* A2B Business Consulting Branding Section */}
          <div className="logo-section">
            <img 
              src="/images/a2b-logo.png" 
              alt="A2B Business Consulting LLC"
              className="a2b-logo"
            />
            <h1 className="company-name">A2B Business Consulting LLC</h1>
            <p className="tagline">Powering Talar Medical Business Intelligence</p>
          </div>
          
          {/* Professional Loading Animation */}
          <div className="loading-section">
            <div className="loading-spinner"></div>
            <p className="loading-text">Authenticating...</p>
            <p className="loading-subtext">Accessing Talar Medical Dashboard</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo-section">
          <img 
            src="/images/a2b-logo.png" 
            alt="A2B Business Consulting LLC"
            className="a2b-logo"
          />
          <h1 className="company-name">A2B Business Consulting LLC</h1>
          <p className="tagline">Secure Access Portal</p>
        </div>

        <div className="login-section">
          <h2 className="login-title">Talar Medical Dashboard</h2>
          <p className="login-subtitle">Enter your credentials to continue</p>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username" className="form-label">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                placeholder="Enter your username"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="Enter your password"
                required
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <button type="submit" className="login-button">
              Sign In
            </button>
          </form>

          <div className="login-footer">
            {/* Demo credentials removed for production deployment */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;